import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ActivateGeoItemDocument,
  type ActivateGeoItemMutation,
  type ActivateGeoItemMutationVariables,
} from "~/features/geoItems/gqls";
import {
  DeactivateGeoItemDocument,
  type DeactivateGeoItemMutation,
  type DeactivateGeoItemMutationVariables,
} from "~/features/recordGeoItems/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Activate / deactivate a standalone geo item (`cnut(De)activateGeoItem`). Returns
 * plain submitters + a `processing` flag; the calling container owns the confirm dialog.
 */
export function useGeoItemActivation() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [activate] = useMutation<ActivateGeoItemMutation, ActivateGeoItemMutationVariables>(ActivateGeoItemDocument);
  const [deactivate] = useMutation<DeactivateGeoItemMutation, DeactivateGeoItemMutationVariables>(
    DeactivateGeoItemDocument,
  );

  const run = useCallback(
    async (id: string, shouldDeactivate: boolean) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        if (shouldDeactivate) {
          await deactivate({ variables: { input: { tenantId, id } } });
        } else {
          await activate({ variables: { input: { tenantId, id } } });
        }
        toast.success(
          shouldDeactivate
            ? t("feature.common.deactivatedSuccessfully", { model: "geoItem" })
            : t("feature.common.activatedSuccessfully", { model: "geoItem" }),
        );
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [activate, deactivate, tenantId, t],
  );

  const onActivate = useCallback((id: string) => run(id, false), [run]);
  const onDeactivate = useCallback((id: string) => run(id, true), [run]);

  return { onActivate, onDeactivate, processing };
}
